import { type ExtendShiftConfig, ExtendShiftState } from "@clipboard-health/flag-backend-main";

export interface ExtendShiftConfigResult {
  isExtendShiftFeatureEnabled: boolean;
  isShiftExtendable: boolean;
  maxExtensionInMinutes: number | undefined;
  isCapAtShiftLength: boolean;
}

const DISABLED_RESULT: ExtendShiftConfigResult = {
  isExtendShiftFeatureEnabled: false,
  isShiftExtendable: false,
  maxExtensionInMinutes: undefined,
  isCapAtShiftLength: false,
};

export function getExtendShiftConfig(
  extendShiftConfig: ExtendShiftConfig | undefined
): ExtendShiftConfigResult {
  if (!extendShiftConfig) {
    return DISABLED_RESULT;
  }

  const { state } = extendShiftConfig;

  // Feature flag is set to disabled; no shift extension functionality
  if (state === ExtendShiftState.DISABLED) {
    return DISABLED_RESULT;
  }

  // Feature is active but shift extension UI is hidden; used for observability-only rollout
  if (state === ExtendShiftState.SHIFT_EXTENSION_DISABLED) {
    return {
      isExtendShiftFeatureEnabled: true,
      isShiftExtendable: false,
      maxExtensionInMinutes: undefined,
      isCapAtShiftLength: false,
    };
  }

  const maxExtensionInMinutes =
    "maxExtensionInMinutes" in extendShiftConfig &&
    typeof extendShiftConfig.maxExtensionInMinutes === "number"
      ? extendShiftConfig.maxExtensionInMinutes
      : undefined;

  // Typed enum variant: scheduled end = clockIn + shift duration (late clock-ins get full duration)
  if (state === ExtendShiftState.SHIFT_LENGTH_CAPPED) {
    return {
      isExtendShiftFeatureEnabled: true,
      isShiftExtendable: true,
      maxExtensionInMinutes,
      isCapAtShiftLength: true,
    };
  }

  // Workers can request extensions; scheduled end is the original shift end
  if (state === ExtendShiftState.SHIFT_EXTENSION_ENABLED) {
    return {
      isExtendShiftFeatureEnabled: true,
      isShiftExtendable: true,
      maxExtensionInMinutes,
      isCapAtShiftLength: false,
    };
  }

  // String variant of cap-at-shift-length (pre-enum migration); same behavior as SHIFT_LENGTH_CAPPED
  if (state === ExtendShiftState.CAP_AT_SHIFT_LENGTH) {
    return {
      isExtendShiftFeatureEnabled: true,
      isShiftExtendable: true,
      maxExtensionInMinutes,
      isCapAtShiftLength: true,
    };
  }

  return DISABLED_RESULT;
}
